import { create } from 'zustand'
import type { VTTState, ToolMode, FogMode, MeasurementShape, Barrier, FeedMessage, MapConfig, Token, LightSource } from '@/types'
import type { Point } from '@/utils/geometry'

/**
 * A live ruler measurement broadcast by another participant. Transient —
 * never persisted, lives only in the store while the peer is measuring and
 * is removed the moment they stop (active=false) or the map changes.
 */
export interface RemoteRuler {
  userId: string
  displayName: string
  mapId: string
  start: { x: number; y: number }
  end: { x: number; y: number }
  /** Measurement-template shape this peer is drawing. Defaults to 'line'
   *  when the relay payload omits it (back-compat with pre-shape clients).
   *  The overlay recomputes the geometry locally from start+end+shape. */
  shape: MeasurementShape
}

/**
 * A transient ping marker — a temporary "look here" pulse placed by a
 * left-click-and-hold on empty canvas. Visible to every participant in the
 * session in real time, never persisted (no DB row, no schema). Lives in the
 * store only until its TTL elapses, then it's pruned and the PIXI layer stops
 * drawing it. Both local and remote pings flow through here.
 */
export interface PingMarker {
  /** Unique id (userId + timestamp) so concurrent pings never collide. */
  id: string
  userId: string
  mapId: string
  /** World-space coordinates (PIXI stage space, post-viewport-transform). */
  x: number
  y: number
  /** performance.now()-domain timestamp when the ping was created locally. */
  createdAt: number
}

/** How long a ping marker lives before it auto-expires (ms). */
export const PING_TTL_MS = 3000

/**
 * Phase 1-6 VTT Store
 * Manages application state for local and networked token dragging with multi-map support
 */
interface VTTStore extends VTTState {
  // Computed getters
  getActiveMap: () => MapConfig | null
  getActiveMapTokens: () => Token[]
  getActiveMapBarriers: () => Barrier[]
  // Actions
  startDrag: (tokenId: string, startX: number, startY: number) => void
  updateDrag: (currentX: number, currentY: number) => void
  endDrag: (finalX: number, finalY: number) => void
  cancelDrag: () => void
  moveToken: (tokenId: string, x: number, y: number, waypoints?: Array<{ x: number; y: number }>, instant?: boolean) => void
  // Selection helpers. `selectToken` replaces the selection with a
  // single id (or clears it when passed null) — convenience for the
  // common click-without-modifier path. `setSelectedTokenIds` replaces
  // the entire set; `toggleSelectedTokenId` flips a single id in/out
  // (shift-click); `clearSelectedTokens` empties the set.
  selectToken: (tokenId: string | null) => void
  setSelectedTokenIds: (tokenIds: string[]) => void
  toggleSelectedTokenId: (tokenId: string) => void
  clearSelectedTokens: () => void
  setTokens: (tokens: VTTState['tokens']) => void
  upsertToken: (token: Token) => void
  removeToken: (tokenId: string) => void
  // Bulk-remove a set of tokens in one store update. Used by the
  // `tokens_deleted` socket broadcast for bulk delete; same cleanup
  // as removeToken (clears selection / waypoints / instant flags).
  removeTokens: (tokenIds: string[]) => void
  // Phase 6: Map management
  setMaps: (maps: MapConfig[]) => void
  setActiveMapId: (mapId: string | null) => void
  addMap: (map: MapConfig) => void
  updateMap: (mapId: string, updates: Partial<MapConfig>) => void
  removeMap: (mapId: string) => void
  switchMap: (mapId: string) => void
  // Temporary storage for waypoints (cleared after animation starts)
  tokenWaypoints: Map<string, Array<{ x: number; y: number }>>
  clearWaypoints: (tokenId: string) => void
  // Phase 3c: Track instant moves (keyboard moves from server)
  instantMoves: Set<string> // Token IDs that should animate instantly
  clearInstantMove: (tokenId: string) => void
  // VTT #526: per-token, owner-only "hide my own view of this token's HP bar".
  // Purely a client-side view preference — never sent to the server, only
  // affects the owner's own screen. Persisted to localStorage across reloads.
  ownHpBarHidden: Set<string>
  toggleOwnHpBarHidden: (tokenId: string) => void
  // Phase 3: Tool mode and vision settings
  toolMode: ToolMode
  setToolMode: (mode: ToolMode) => void
  /** Reveal-vs-hide sub-setting for the fog brush. Only meaningful when
   *  `toolMode === 'fog'`; persists across tool switches so the GM
   *  doesn't lose their last setting when toggling tools. */
  fogMode: FogMode
  setFogMode: (mode: FogMode) => void
  /** Active measurement-template shape for the ruler tool. Defaults to
   *  `line` (classic straight ruler); persists across tool switches so
   *  re-arming the ruler keeps the last chosen shape. Only meaningful
   *  when `toolMode === 'ruler'`. */
  measurementShape: MeasurementShape
  setMeasurementShape: (shape: MeasurementShape) => void
  setLosEnabled: (enabled: boolean) => void
  setShowBarriers: (show: boolean) => void
  setRenderMode: (mode: 'dimmed' | 'darkness') => void
  // Phase 3: Barrier management
  addBarrier: (barrier: Barrier) => void
  removeBarrier: (barrierId: string) => void
  updateBarrier: (barrierId: string, updates: Partial<Barrier>) => void
  setBarriers: (barriers: Barrier[]) => void
  /**
   * Replace one barrier with another, preserving list order. Used by the
   * `barrier_created` WebSocket handler to swap an optimistic client-side
   * barrier for the server-confirmed version (which has the canonical ID)
   * without leaving a duplicate in the store. If no barrier with `oldId`
   * exists, the new barrier is appended.
   */
  replaceBarrier: (oldId: string, newBarrier: Barrier) => void
  // "See as token": tokenId the GM is currently viewing the board as (their
  // view is occluded to that token's fog + line-of-sight), or null for the
  // GM's own full view.
  previewingVisionFor: string | null
  setPreviewingVisionFor: (tokenId: string | null) => void
  // Phase 3: Barrier selection
  selectedBarrierId: string | null
  selectBarrier: (barrierId: string | null) => void
  // Phase 4: Fog of war (manual_fog mode)
  fog: Map<string, boolean> // Map<"x,y", true> - revealed cells
  brushSize: number // Brush radius in grid squares (1-5)
  setFog: (fog: Map<string, boolean>) => void
  revealFogCells: (cells: Array<{ x: number; y: number }>) => void
  hideFogCells: (cells: Array<{ x: number; y: number }>) => void
  setBrushSize: (size: number) => void
  // Phase 3b: Vision polygon cache (dynamic_los mode)
  visionPolygon: Point[] | null // Combined party vision polygon (in pixel coordinates)
  setVisionPolygon: (polygon: Point[] | null) => void
  visionNeedsRecalculation: boolean // Flag to trigger vision recalculation
  triggerVisionRecalculation: () => void
  // Phase 5: Chat and dice
  messages: FeedMessage[] // Chat messages and dice rolls
  addMessage: (message: FeedMessage) => void
  setMessages: (messages: FeedMessage[]) => void
  clearMessages: () => void
  // Character sheet modal — the characterId whose full sheet is open in
  // the Foundry-style modal, or null when closed. Opened from the
  // sidebar character rows and the right-click token tooltip; the modal
  // itself is mounted once at the app root and reads this id.
  characterSheetId: string | null
  openCharacterSheet: (characterId: string) => void
  closeCharacterSheet: () => void
  // Stats-edit modal (#657) — the characterId of the token whose max HP /
  // AC / initiative are being GM-corrected, or null when closed. Opened
  // from the right-click token tooltip; mounted once at the app root
  // alongside the character-sheet modal above.
  statsEditCharacterId: string | null
  openStatsEdit: (characterId: string) => void
  closeStatsEdit: () => void
  // GM handoff offer — set when a `gm_handoff_offered` event arrives for
  // this client (target of the offer). The handoff toast reads it and
  // renders Accept / Decline. Cleared by accept, decline, expiry, or
  // role_changed.
  gmHandoffOffer:
    | { fromUserId: string; fromDisplayName: string; expiresAt: number }
    | null
  setGmHandoffOffer: (
    offer:
      | { fromUserId: string; fromDisplayName: string; expiresAt: number }
      | null
  ) => void
  // Transient ruler relay — other participants' live measurements, keyed by
  // userId. Purely ephemeral UI state (never persisted). `upsertRemoteRuler`
  // is driven by the `ruler_updated` broadcast: it adds/updates an active
  // ruler and removes it when the peer stops measuring. `clearRemoteRuler`
  // and `clearRemoteRulers` drop entries on disconnect / map switch.
  remoteRulers: Map<string, RemoteRuler>
  upsertRemoteRuler: (
    // `shape` is optional at the call boundary for back-compat — the action
    // defaults it to 'line' if a pre-shape relay frame omits it.
    ruler: Omit<RemoteRuler, 'shape'> & { active: boolean; shape?: MeasurementShape }
  ) => void
  clearRemoteRuler: (userId: string) => void
  clearRemoteRulers: () => void
  // Transient ping markers (left-hold pulses), keyed by ping id. Purely
  // ephemeral — never persisted. `addPing` is driven both by the local
  // long-press and by the `ping_placed` socket broadcast; `prunePings`
  // drops entries older than PING_TTL_MS (called from the canvas ticker);
  // `clearPings` wipes them on map switch (world coords no longer apply).
  activePings: Map<string, PingMarker>
  addPing: (ping: PingMarker) => void
  prunePings: (now: number) => void
  clearPings: () => void
  // Dynamic lighting — standalone light sources (GM-placed) and active
  // token lights. `lightSources` holds the map-indexed list hydrated from
  // session_state and kept live via light_source_* socket events.
  lightSources: LightSource[]
  setLightSources: (sources: LightSource[]) => void
  addLightSource: (source: LightSource) => void
  removeLightSource: (lightSourceId: string) => void
  updateLightSourceInStore: (lightSourceId: string, fields: { radius?: number; x?: number; y?: number; brightRadius?: number; dimRadius?: number; color?: string; falloff?: number }) => void
  // The standalone light source currently selected for editing (GM), or null.
  selectedLightId: string | null
  setSelectedLightId: (lightSourceId: string | null) => void
  // Toggle dynamic lighting on the active map.
  setLightingEnabled: (enabled: boolean) => void
  // Token clipboard for Ctrl+C/V copy-paste. Stores the source token id.
  // null = nothing on clipboard.
  tokenClipboard: string | null
  setTokenClipboard: (tokenId: string | null) => void
}

// VTT #526: persist the owner's per-token "hide my own HP bar" view preference
// across reloads. Client-only; guarded so a missing/broken localStorage (SSR,
// private mode) degrades to an empty set rather than throwing.
const OWN_HP_BAR_HIDDEN_KEY = 'vtt:ownHpBarHidden'

function loadOwnHpBarHidden(): Set<string> {
  try {
    const raw = localStorage.getItem(OWN_HP_BAR_HIDDEN_KEY)
    if (!raw) return new Set()
    const arr = JSON.parse(raw)
    return Array.isArray(arr) ? new Set(arr.filter((x) => typeof x === 'string')) : new Set()
  } catch {
    return new Set()
  }
}

function saveOwnHpBarHidden(ids: Set<string>): void {
  try {
    localStorage.setItem(OWN_HP_BAR_HIDDEN_KEY, JSON.stringify([...ids]))
  } catch {
    // best-effort; the in-memory set still drives this session
  }
}

export const useVTTStore = create<VTTStore>((set, get) => ({
  // Initial state — empty until the server hydrates real session state.
  // No placeholder map/tokens: the tabletop is campaign-scoped and a
  // brand-new campaign legitimately has zero maps, in which case App
  // renders <BlankGrid> instead of the canvas.
  maps: [],
  activeMapId: null,
  tokens: [],
  dragState: null,
  selectedTokenIds: [],
  tokenWaypoints: new Map(),
  instantMoves: new Set(), // Phase 3c: track instant keyboard moves from server
  ownHpBarHidden: loadOwnHpBarHidden(), // VTT #526: owner's own-view HP-bar opt-out
  toolMode: 'select', // Phase 3: default to select mode
  fogMode: 'reveal',  // default fog brush direction
  measurementShape: 'line', // default ruler shape: classic straight line
  barriers: [], // Phase 3: vision-blocking barriers
  previewingVisionFor: null, // Phase 3: no vision preview by default
  selectedBarrierId: null, // Phase 3: no barrier selected by default
  fog: new Map(), // Phase 4: fog of war revealed cells
  brushSize: 3, // Phase 4: default brush size
  visionPolygon: null, // Phase 3b: no vision polygon initially
  visionNeedsRecalculation: false, // Phase 3b: no recalculation needed initially
  messages: [], // Phase 5: chat and dice messages
  characterSheetId: null, // Character sheet modal closed by default
  statsEditCharacterId: null, // Stats-edit modal closed by default (#657)
  gmHandoffOffer: null, // No pending GM handoff offer by default
  remoteRulers: new Map(), // Transient: other participants' live rulers
  activePings: new Map(), // Transient: active ping markers (local + remote)
  lightSources: [], // Dynamic lighting: standalone GM-placed light sources
  selectedLightId: null, // Dynamic lighting: no light selected by default

  // Phase 6: Computed getters
  getActiveMap: () => {
    const state = get()
    return state.maps.find(m => m.id === state.activeMapId) || null
  },

  getActiveMapTokens: () => {
    const state = get()
    if (!state.activeMapId) return []
    return state.tokens.filter(t => t.mapId === state.activeMapId)
  },

  getActiveMapBarriers: () => {
    const state = get()
    if (!state.activeMapId) return []
    return state.barriers.filter(b => b.mapId === state.activeMapId)
  },

  // Start dragging a token. If the token is already in the current
  // selection, keep the full selection (so a bulk drag stays bulk);
  // otherwise replace the selection with just this token.
  startDrag: (tokenId: string, startX: number, startY: number) => {
    set((state) => ({
      dragState: {
        tokenId,
        startX,
        startY,
        currentX: startX,
        currentY: startY,
        isDragging: true,
      },
      selectedTokenIds: state.selectedTokenIds.includes(tokenId)
        ? state.selectedTokenIds
        : [tokenId],
    }))
  },

  // Update drag position (while dragging)
  updateDrag: (currentX: number, currentY: number) => {
    set((state) => {
      if (!state.dragState) return state
      return {
        dragState: {
          ...state.dragState,
          currentX,
          currentY,
        },
      }
    })
  },

  // End drag and update token position
  endDrag: (finalX: number, finalY: number) => {
    set((state) => {
      if (!state.dragState) return state

      const { tokenId } = state.dragState

      return {
        dragState: null,
        tokens: state.tokens.map((token) =>
          token.id === tokenId ? { ...token, x: finalX, y: finalY } : token
        ),
      }
    })
  },

  // Cancel drag and return to original position
  cancelDrag: () => {
    set({ dragState: null })
  },

  // Move token to new position (programmatically)
  // Phase 3c: Added instant parameter for keyboard moves from server
  moveToken: (tokenId: string, x: number, y: number, waypoints?: Array<{ x: number; y: number }>, instant?: boolean) => {
    set((state) => {
      // Store waypoints if provided
      const newWaypoints = new Map(state.tokenWaypoints)
      if (waypoints && waypoints.length > 0) {
        newWaypoints.set(tokenId, waypoints)
      }

      // Track instant moves
      const newInstantMoves = new Set(state.instantMoves)
      if (instant) {
        newInstantMoves.add(tokenId)
      }

      return {
        tokens: state.tokens.map((token) =>
          token.id === tokenId ? { ...token, x, y } : token
        ),
        tokenWaypoints: newWaypoints,
        instantMoves: newInstantMoves,
        visionNeedsRecalculation: true, // Phase 3b: trigger vision recalculation
      }
    })
  },

  // Clear waypoints for a token (called after animation starts)
  clearWaypoints: (tokenId: string) => {
    set((state) => {
      const newWaypoints = new Map(state.tokenWaypoints)
      newWaypoints.delete(tokenId)
      return { tokenWaypoints: newWaypoints }
    })
  },

  // Phase 3c: Clear instant move flag
  clearInstantMove: (tokenId: string) => {
    set((state) => {
      const newInstantMoves = new Set(state.instantMoves)
      newInstantMoves.delete(tokenId)
      return { instantMoves: newInstantMoves }
    })
  },

  // Replace the selection with a single id, or clear it when passed
  // null. Convenience wrapper around setSelectedTokenIds for the
  // common click-without-modifier path.
  // Selection is single-authority: token, light, and barrier are mutually
  // exclusive on the board. Selecting any one drops the others so their
  // canvas highlight/HUD can't linger (e.g. clicking a token off a light).
  selectToken: (tokenId: string | null) => {
    set(
      tokenId == null
        ? { selectedTokenIds: [] }
        : {
            selectedTokenIds: [tokenId],
            selectedLightId: null,
            selectedBarrierId: null,
          },
    )
  },

  // Replace the entire selection set in one shot. Used by rubber-band
  // commit and by shift+rubber-band (which passes the union).
  setSelectedTokenIds: (tokenIds: string[]) => {
    set(
      tokenIds.length === 0
        ? { selectedTokenIds: tokenIds }
        : {
            selectedTokenIds: tokenIds,
            selectedLightId: null,
            selectedBarrierId: null,
          },
    )
  },

  // Toggle a single id in/out of the selection (shift-click on token).
  toggleSelectedTokenId: (tokenId: string) => {
    set((state) => {
      const idx = state.selectedTokenIds.indexOf(tokenId)
      if (idx === -1) {
        return {
          selectedTokenIds: [...state.selectedTokenIds, tokenId],
          selectedLightId: null,
          selectedBarrierId: null,
        }
      }
      const next = state.selectedTokenIds.slice()
      next.splice(idx, 1)
      return { selectedTokenIds: next }
    })
  },

  // VTT #526: flip the owner's "hide my own view of this token's HP bar"
  // preference. Client-only + persisted; the render gate (shouldShowHPBar)
  // reads this set for owned tokens.
  toggleOwnHpBarHidden: (tokenId) => {
    set((state) => {
      const next = new Set(state.ownHpBarHidden)
      if (next.has(tokenId)) next.delete(tokenId)
      else next.add(tokenId)
      saveOwnHpBarHidden(next)
      return { ownHpBarHidden: next }
    })
  },

  // Clear the selection set. Used by click-on-empty-canvas and Escape.
  clearSelectedTokens: () => {
    set({ selectedTokenIds: [] })
  },

  // Set all tokens (for WebSocket hydration)
  setTokens: (tokens) => {
    set({ tokens })
  },

  // Add or replace a token by id. Used by the `token_created` WebSocket
  // handler; dedupes so a spawn that echoes back doesn't double-insert.
  upsertToken: (token: Token) => {
    set((state) => {
      const idx = state.tokens.findIndex((t) => t.id === token.id)
      if (idx === -1) {
        return { tokens: [...state.tokens, token], visionNeedsRecalculation: true }
      }
      const next = state.tokens.slice()
      next[idx] = token
      return { tokens: next, visionNeedsRecalculation: true }
    })
  },

  // Drop a token by id. Used by the `token_deleted` WebSocket handler.
  // Clears any incidental references (selection, waypoints, in-flight
  // animation markers) so a deleted token doesn't keep ghost state.
  removeToken: (tokenId: string) => {
    set((state) => {
      const next = state.tokens.filter((t) => t.id !== tokenId)
      if (next.length === state.tokens.length) return {}
      const nextWaypoints = new Map(state.tokenWaypoints)
      nextWaypoints.delete(tokenId)
      const nextInstant = new Set(state.instantMoves)
      nextInstant.delete(tokenId)
      return {
        tokens: next,
        tokenWaypoints: nextWaypoints,
        instantMoves: nextInstant,
        selectedTokenIds: state.selectedTokenIds.filter((id) => id !== tokenId),
        previewingVisionFor:
          state.previewingVisionFor === tokenId
            ? null
            : state.previewingVisionFor,
        visionNeedsRecalculation: true,
      }
    })
  },

  // Bulk-drop a set of tokens in one store update. Mirrors removeToken
  // but operates over a Set so the bulk `tokens_deleted` broadcast
  // doesn't trigger N separate renders.
  removeTokens: (tokenIds: string[]) => {
    if (tokenIds.length === 0) return
    set((state) => {
      const drop = new Set(tokenIds)
      const next = state.tokens.filter((t) => !drop.has(t.id))
      if (next.length === state.tokens.length) return {}
      const nextWaypoints = new Map(state.tokenWaypoints)
      const nextInstant = new Set(state.instantMoves)
      drop.forEach((id) => {
        nextWaypoints.delete(id)
        nextInstant.delete(id)
      })
      return {
        tokens: next,
        tokenWaypoints: nextWaypoints,
        instantMoves: nextInstant,
        selectedTokenIds: state.selectedTokenIds.filter((id) => !drop.has(id)),
        previewingVisionFor:
          state.previewingVisionFor && drop.has(state.previewingVisionFor)
            ? null
            : state.previewingVisionFor,
        visionNeedsRecalculation: true,
      }
    })
  },

  // Phase 6: Set all maps (for WebSocket hydration)
  setMaps: (maps: MapConfig[]) => {
    set({ maps })
  },

  // Phase 6: Set active map ID
  setActiveMapId: (mapId: string | null) => {
    set({ activeMapId: mapId, visionNeedsRecalculation: true })
  },

  // Phase 6: Add a new map
  addMap: (map: MapConfig) => {
    set((state) => ({
      maps: [...state.maps, map],
    }))
  },

  // Phase 6: Update a map
  updateMap: (mapId: string, updates: Partial<MapConfig>) => {
    set((state) => ({
      maps: state.maps.map((m) =>
        m.id === mapId ? { ...m, ...updates } : m
      ),
      visionNeedsRecalculation: true,
    }))
  },

  // Phase 6: Remove a map
  removeMap: (mapId: string) => {
    set((state) => ({
      maps: state.maps.filter((m) => m.id !== mapId),
      activeMapId: state.activeMapId === mapId ? null : state.activeMapId,
    }))
  },

  // Phase 6: Switch to a different map
  switchMap: (mapId: string) => {
    set((state) => {
      const mapExists = state.maps.some((m) => m.id === mapId)
      if (!mapExists) return state

      return {
        activeMapId: mapId,
        visionNeedsRecalculation: true,
        selectedTokenIds: [], // Deselect tokens when switching maps
        selectedBarrierId: null, // Deselect barrier when switching maps
      }
    })
  },

  // Phase 3: Set tool mode
  setToolMode: (mode: ToolMode) => {
    set({ toolMode: mode })
  },

  setFogMode: (mode: FogMode) => {
    set({ fogMode: mode })
  },

  setMeasurementShape: (shape: MeasurementShape) => {
    set({ measurementShape: shape })
  },

  // Toggle dynamic line-of-sight on the active map. Independent of fog —
  // fog is always available and layers on top of LoS-occluded darkness.
  setLosEnabled: (enabled: boolean) => {
    set((state) => {
      if (!state.activeMapId) return state
      return {
        maps: state.maps.map((m) =>
          m.id === state.activeMapId ? { ...m, losEnabled: enabled } : m
        ),
      }
    })
  },

  // Fog render mode for the active map — 'darkness' (opaque, hides content)
  // vs 'dimmed' (semi-transparent, terrain shows through). Player-facing only.
  setRenderMode: (mode: 'dimmed' | 'darkness') => {
    set((state) => {
      if (!state.activeMapId) return state
      return {
        maps: state.maps.map((m) =>
          m.id === state.activeMapId ? { ...m, renderMode: mode } : m
        ),
      }
    })
  },

  // Phase 3: Toggle barrier visibility (updates active map)
  setShowBarriers: (show: boolean) => {
    set((state) => {
      if (!state.activeMapId) return state
      return {
        maps: state.maps.map((m) =>
          m.id === state.activeMapId ? { ...m, showBarriers: show } : m
        ),
      }
    })
  },

  // Phase 3: Add a barrier
  addBarrier: (barrier: Barrier) => {
    set((state) => ({
      barriers: [...state.barriers, barrier],
      visionNeedsRecalculation: true, // Phase 3b: trigger vision recalculation
    }))
  },

  // Phase 3: Remove a barrier
  removeBarrier: (barrierId: string) => {
    set((state) => ({
      barriers: state.barriers.filter((b) => b.id !== barrierId),
      visionNeedsRecalculation: true, // Phase 3b: trigger vision recalculation
    }))
  },

  // Phase 3: Update a barrier
  updateBarrier: (barrierId: string, updates: Partial<Barrier>) => {
    set((state) => ({
      barriers: state.barriers.map((b) =>
        b.id === barrierId ? { ...b, ...updates } : b
      ),
      visionNeedsRecalculation: true, // Phase 3b: trigger vision recalculation
    }))
  },

  // Phase 3: Set all barriers (for WebSocket hydration)
  setBarriers: (barriers: Barrier[]) => {
    set({ barriers, visionNeedsRecalculation: true }) // Phase 3b: trigger vision recalculation
  },

  // Swap an optimistic barrier for the server-confirmed version. Falls back
  // to append if no match — matches addBarrier's behavior for that case.
  replaceBarrier: (oldId: string, newBarrier: Barrier) => {
    set((state) => {
      const idx = state.barriers.findIndex((b) => b.id === oldId)
      if (idx === -1) {
        return {
          barriers: [...state.barriers, newBarrier],
          visionNeedsRecalculation: true,
        }
      }
      const next = state.barriers.slice()
      next[idx] = newBarrier
      return {
        barriers: next,
        visionNeedsRecalculation: true,
      }
    })
  },

  // Phase 3: Set vision preview
  setPreviewingVisionFor: (tokenId: string | null) => {
    set({ previewingVisionFor: tokenId })
  },

  // Phase 3: Select barrier
  selectBarrier: (barrierId: string | null) => {
    // Selecting a barrier drops any token/light selection (single-authority).
    set(
      barrierId == null
        ? { selectedBarrierId: null }
        : {
            selectedBarrierId: barrierId,
            selectedTokenIds: [],
            selectedLightId: null,
          },
    )
  },

  // Phase 4: Set fog data (for hydration)
  setFog: (fog: Map<string, boolean>) => {
    set({ fog })
  },

  // Phase 4: Reveal fog cells
  revealFogCells: (cells: Array<{ x: number; y: number }>) => {
    set((state) => {
      const newFog = new Map(state.fog)
      cells.forEach((cell) => {
        const key = `${cell.x},${cell.y}`
        newFog.set(key, true)
      })
      return { fog: newFog }
    })
  },

  // Phase 4: Hide fog cells
  hideFogCells: (cells: Array<{ x: number; y: number }>) => {
    set((state) => {
      const newFog = new Map(state.fog)
      cells.forEach((cell) => {
        const key = `${cell.x},${cell.y}`
        newFog.delete(key)
      })
      return { fog: newFog }
    })
  },

  // Phase 4: Set brush size
  setBrushSize: (size: number) => {
    set({ brushSize: Math.max(1, Math.min(5, size)) }) // Clamp between 1-5
  },

  // Phase 3b: Set vision polygon
  setVisionPolygon: (polygon: Point[] | null) => {
    set({ visionPolygon: polygon, visionNeedsRecalculation: false })
  },

  // Phase 3b: Trigger vision recalculation
  triggerVisionRecalculation: () => {
    set({ visionNeedsRecalculation: true })
  },

  // Phase 5: Add a message to the feed
  addMessage: (message: FeedMessage) => {
    set((state) => ({
      messages: [...state.messages, message],
    }))
  },

  // Replace the entire feed in one shot. Used by the `chat_history` replay
  // on session-join to seed prior messages before live events arrive.
  setMessages: (messages: FeedMessage[]) => {
    set({ messages })
  },

  // Phase 5: Clear all messages
  clearMessages: () => {
    set({ messages: [] })
  },

  // Open the full character sheet modal for a given character.
  openCharacterSheet: (characterId: string) => {
    set({ characterSheetId: characterId })
  },

  // Close the character sheet modal.
  closeCharacterSheet: () => {
    set({ characterSheetId: null })
  },

  // Open the stats-edit modal (#657) for a given character.
  openStatsEdit: (characterId: string) => {
    set({ statsEditCharacterId: characterId })
  },

  // Close the stats-edit modal.
  closeStatsEdit: () => {
    set({ statsEditCharacterId: null })
  },

  // Set or clear the inbound GM handoff offer for this client.
  setGmHandoffOffer: (offer) => {
    set({ gmHandoffOffer: offer })
  },

  // Apply a `ruler_updated` broadcast. An active ruler is upserted (keyed
  // by userId, so one peer never stacks multiple lines); an inactive one
  // removes that peer's entry. Always returns a fresh Map so React notices.
  upsertRemoteRuler: (ruler) => {
    set((state) => {
      const next = new Map(state.remoteRulers)
      if (!ruler.active) {
        if (!next.has(ruler.userId)) return state
        next.delete(ruler.userId)
        return { remoteRulers: next }
      }
      next.set(ruler.userId, {
        userId: ruler.userId,
        displayName: ruler.displayName,
        mapId: ruler.mapId,
        start: ruler.start,
        end: ruler.end,
        shape: ruler.shape ?? 'line',
      })
      return { remoteRulers: next }
    })
  },

  // Drop one peer's ruler (e.g. they disconnected).
  clearRemoteRuler: (userId) => {
    set((state) => {
      if (!state.remoteRulers.has(userId)) return state
      const next = new Map(state.remoteRulers)
      next.delete(userId)
      return { remoteRulers: next }
    })
  },

  // Drop every remote ruler (e.g. map switch — world coords no longer apply).
  clearRemoteRulers: () => {
    set((state) => {
      if (state.remoteRulers.size === 0) return state
      return { remoteRulers: new Map() }
    })
  },

  // Add a ping marker (local long-press or remote `ping_placed`). Keyed by
  // id so concurrent pings coexist. Always returns a fresh Map so React /
  // the ticker notice the change.
  addPing: (ping) => {
    set((state) => {
      const next = new Map(state.activePings)
      next.set(ping.id, ping)
      return { activePings: next }
    })
  },

  // Drop any ping older than PING_TTL_MS. Called from the canvas animation
  // ticker each frame; no-ops (returns the same state) when nothing expired
  // so it doesn't churn renders.
  prunePings: (now) => {
    set((state) => {
      if (state.activePings.size === 0) return state
      let removed = false
      const next = new Map(state.activePings)
      for (const [id, ping] of next) {
        if (now - ping.createdAt >= PING_TTL_MS) {
          next.delete(id)
          removed = true
        }
      }
      return removed ? { activePings: next } : state
    })
  },

  // Wipe all pings (e.g. map switch).
  clearPings: () => {
    set((state) => {
      if (state.activePings.size === 0) return state
      return { activePings: new Map() }
    })
  },

  // ── Dynamic Lighting ────────────────────────────────────────────────────────

  // Replace the entire light source list (session hydration).
  setLightSources: (sources) => {
    set({ lightSources: sources })
  },

  // Append a new light source (light_source_created broadcast).
  addLightSource: (source) => {
    set((state) => ({ lightSources: [...state.lightSources, source] }))
  },

  // Drop a light source (light_source_deleted broadcast). Clears the selection
  // if the removed light was the selected one.
  removeLightSource: (lightSourceId) => {
    set((state) => ({
      lightSources: state.lightSources.filter((ls) => ls.id !== lightSourceId),
      selectedLightId:
        state.selectedLightId === lightSourceId ? null : state.selectedLightId,
    }))
  },

  // Update an existing light source's radius and/or position
  // (light_source_updated broadcast, or an optimistic local edit). Only the
  // supplied fields are applied — undefined keys are ignored.
  updateLightSourceInStore: (lightSourceId, fields) => {
    const patch = Object.fromEntries(
      Object.entries(fields).filter(([, v]) => v !== undefined)
    )
    set((state) => ({
      lightSources: state.lightSources.map((ls) =>
        ls.id === lightSourceId ? { ...ls, ...patch } : ls
      ),
    }))
  },

  // Select / deselect a standalone light source for editing.
  setSelectedLightId: (lightSourceId) => {
    // Selecting a light drops any token/barrier selection (single-authority).
    set(
      lightSourceId == null
        ? { selectedLightId: null }
        : {
            selectedLightId: lightSourceId,
            selectedTokenIds: [],
            selectedBarrierId: null,
          },
    )
  },

  // Toggle dynamic lighting on the active map.
  setLightingEnabled: (enabled) => {
    set((state) => {
      if (!state.activeMapId) return state
      return {
        maps: state.maps.map((m) =>
          m.id === state.activeMapId ? { ...m, lightingEnabled: enabled } : m
        ),
      }
    })
  },
  // Token clipboard for Ctrl+C/V copy-paste.
  tokenClipboard: null,
  setTokenClipboard: (tokenId) => set({ tokenClipboard: tokenId }),
}))
